import { SampleType } from '../../../types/graphql-global-types'
import { Presetting, SampleFormData } from './types'

/**
 * Defines the default presetting values (methodId and unitId) for various soil analysis parameters.
 * These UUIDs link to specific analysis methods and units within the system.
 */
export const presettingSoilValues: Presetting = {
  // pH (Hydrogen Potential)
  '84490c73-d6a8-487e-9fcc-cbdd8a0be609': {
    methodId: '4e69c33f-96ed-49f6-8d8c-8d77bce43616', // Method: H2O
    unitId: 'a48e1d5d-a7aa-4306-8300-eede4b8951e1', // Unit: pH
  },
  // OrgM (Organic Matter)
  '35d95f91-1a57-42d3-810c-65ddadf8cf5b': {
    methodId: '2f40c535-5a49-4953-89af-b555545f1a9a', // Method: Dichromat Oxidation
    unitId: '35b2af84-185a-4821-9d0e-cfbffc41a0bb', // Unit: g/dm3
  },
  // OrgC (Organic Carbon)
  'c6effc63-d536-4811-adb9-41553b668bdf': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '35b2af84-185a-4821-9d0e-cfbffc41a0bb', // Unit: g/dm3
  },
  // N (Nitrogen)
  '880abcda-5ee5-4068-879c-94489be314d5': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // NO3 (Nitrate)
  '7db01982-c8f9-49a5-a905-cac3dc808a51': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // NH4 (Ammonium)
  'e3fb6af8-1f41-4950-baa3-0c62216d0cd0': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // P (Phosphorus)
  'f2c275c4-1522-4524-8747-08ace254b155': {
    methodId: '8b0ed058-03c9-4fa7-b509-3772d302712d', // Method: Resin
    unitId: '03cd6364-fdb3-4960-92ce-9f05282f5d84', // Unit: P mg/dm3
  },
  // K (Potassium)
  '7a1d7c09-13fa-4ea0-b72b-8290663c31d5': {
    methodId: '8b0ed058-03c9-4fa7-b509-3772d302712d', // Method: Resin
    unitId: '68f11bcf-e424-4468-bac2-1c71451b9e55', // Unit: mmolc/dm3
  },
  // Ca (Calcium)
  '5fe008ca-d2d7-48a6-921b-8b53c781a4ab': {
    methodId: '8b0ed058-03c9-4fa7-b509-3772d302712d', // Method: Resin
    unitId: '68f11bcf-e424-4468-bac2-1c71451b9e55', // Unit: mmolc/dm3
  },
  // Mg (Magnesium)
  'd764880a-c5d8-4a9a-ab49-a2b3ba59970d': {
    methodId: '8b0ed058-03c9-4fa7-b509-3772d302712d', // Method: Resin
    unitId: '68f11bcf-e424-4468-bac2-1c71451b9e55', // Unit: mmolc/dm3
  },
  // Al (Aluminum)
  '33c3ea2e-c70d-42ed-8bd5-907ca1ca1b6b': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '68f11bcf-e424-4468-bac2-1c71451b9e55', // Unit: mmolc/dm3
  },
  // S (Sulfur)
  '0163eeff-5d87-4749-bcf7-3e4be6732808': {
    methodId: '20cc908b-3992-4a51-923e-4f0239e572bb', // Method: Monocalcium Phosphate (MCP)
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Na (Sodium)
  '1418f553-f13a-4c7a-a468-d12109e746c6': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8f0b2e88-d239-45e5-aae0-30ed739d6923', // Unit: Na ppm
  },
  // B (Boron)
  '6990195e-b1a3-4218-90db-f38c9cfae633': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Cu (Copper)
  '578e38c1-e641-4aa9-9a8e-6a428e55f1ec': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Fe (Iron)
  '5c7eb818-06c1-4013-b6d2-2f02f1e54d44': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Mn (Manganese)
  '45b614a2-cce3-4c85-9c63-b459396e9aef': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Zn (Zinc)
  'a85856aa-c3cf-433d-8357-4dbe0259d1f9': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Co (Cobalt)
  '9ff4b7ba-87f4-445f-811e-765465989377': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Mo (Molybdenum)
  '953dcd7a-0de1-4997-8360-985a73af3cb6': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // Se (Selenium)
  'fd68879b-030f-42b9-a278-bdb79247f960': {
    methodId: '11c31a6b-1ab6-4f57-8e22-50a14e21ff21', // Method: DTPA
    unitId: '4f201402-09c7-48e3-b0bc-94e67591d288', // Unit: mg/dm3
  },
  // CEC (Cation Exchange Capacity)
  'ede6c4f5-2e87-4179-92ee-d6a09966e51e': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '68f11bcf-e424-4468-bac2-1c71451b9e55', // Unit: mmolc/dm3
  },
  // BsSat (Base Saturation)
  '50fed1e3-3fd7-496d-ba04-e737088eb05a': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // AlSat (Aluminum Saturation)
  '86694f05-1858-42c7-96cd-f5cd4272fc0c': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // KSat (Potassium Saturation)
  'bd0e5d11-03d4-451c-a619-28f285d9e58d': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // MgSat (Magnesium Saturation)
  'd2b9d0a7-766c-476b-87f4-e78f6f3b14c2': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // CaSat (Calcium Saturation)
  '1a600567-e80d-47a8-9cf4-7244d8e6205a': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // Clay (Clay Content)
  '81acf74b-46ee-41c5-9cc0-7e28d9e99060': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: 'c0135f75-00af-4dd3-967d-35d92a9dd927', // Unit: g/Kg
  },
  // PotNMin (Potentially Mineralizable Nitrogen)
  '077cefbf-5c71-4183-ad3b-f01844f08914': {
    methodId: 'c83cbb0e-d6d4-444e-ae71-875630073be0', // Method: Solvita Burst CO2-C
    unitId: 'cc3b05c6-615c-4615-8117-db71f630ee0f', // Unit: kg N/ha
  },
  // CNRatio (Carbon-Nitrogen Ratio)
  '971db100-cbef-4d55-a5b5-6bcda44b282b': {
    methodId: 'd52e3a73-f173-4ce0-ab66-31475264efe8', // Method: Calculation
    unitId: 'd9fa23b8-d975-43c7-8a94-9e7134c9e0b3', // Unit: C:N
  },
  // MBiomass (Microbial Biomass)
  '262d1335-666b-4caa-bb92-b99f2cf974c8': {
    methodId: 'c83cbb0e-d6d4-444e-ae71-875630073be0', // Method: Solvita Burst CO2-C
    unitId: '1df069b5-fa02-4c00-9779-446c68e69a3f', // Unit: mg/Kg
  },
  // SoilResp (Soil Respiration)
  '2d530abe-a750-4829-bd10-76b3f86bddb5': {
    methodId: 'c83cbb0e-d6d4-444e-ae71-875630073be0', // Method: Solvita Burst CO2-C
    unitId: '1df069b5-fa02-4c00-9779-446c68e69a3f', // Unit: mg/Kg
  },
  // SAS (Soil Aggregate Stability)
  '3895c3e5-82a9-4d40-a221-d9432f22cb82': {
    methodId: 'd52e3a73-f173-4ce0-ab66-31475264efe8', // Method: Calculation
    unitId: '459dda50-3567-4fb4-8a46-5ec8072fb1b3', // Unit: SAS
  },
}

export const presettingLeafValues: Presetting = {
  // N (Nitrogen)
  '880abcda-5ee5-4068-879c-94489be314d5': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // NO3 (Nitrate)
  '7db01982-c8f9-49a5-a905-cac3dc808a51': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // P (Phosphorus)
  'f2c275c4-1522-4524-8747-08ace254b155': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // K (Potassium)
  '7a1d7c09-13fa-4ea0-b72b-8290663c31d5': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // Ca (Calcium)
  '5fe008ca-d2d7-48a6-921b-8b53c781a4ab': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // Mg (Magnesium)
  'd764880a-c5d8-4a9a-ab49-a2b3ba59970d': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // Al (Aluminum)
  '33c3ea2e-c70d-42ed-8bd5-907ca1ca1b6b': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // S (Sulfur)
  '0163eeff-5d87-4749-bcf7-3e4be6732808': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // Na (Sodium)
  '1418f553-f13a-4c7a-a468-d12109e746c6': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '64d915ec-e4ed-437f-aad3-f9ac2ca92373', // Unit: %
  },
  // B (Boron)
  '6990195e-b1a3-4218-90db-f38c9cfae633': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // Cu (Copper)
  '578e38c1-e641-4aa9-9a8e-6a428e55f1ec': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // Fe (Iron)
  '5c7eb818-06c1-4013-b6d2-2f02f1e54d44': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // Mn (Manganese)
  '45b614a2-cce3-4c85-9c63-b459396e9aef': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // Zn (Zinc)
  'a85856aa-c3cf-433d-8357-4dbe0259d1f9': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
  // Mo (Molybdenum)
  '953dcd7a-0de1-4997-8360-985a73af3cb6': {
    methodId: '49b5c641-c53e-4c00-bc8b-e5ff0f209a9e', // Method: Default
    unitId: '8ade87ed-b74a-440a-8b1b-6af213bf5218', // Unit: ppm
  },
}

const basicForm = {
  sampleDate: new Date().toISOString(),
  sampleName: '',
  labName: '',
  // fields are optional
  fieldName: undefined,
  geolocationLat: undefined,
  geolocationLong: undefined,
  growthStageId: undefined,
  filename: undefined,
  clientName: undefined,
  countryId: undefined,
  cropRegionId: undefined,
  address: undefined,
  farmName: undefined,
  farmerName: undefined,
  labId: undefined,
  regionId: undefined,
  results: {},
}
/**
 * Defines a default `SampleFormData` object for a new soil sample.
 * This structure provides initial values for a form, ensuring consistency.
 */
export const soilDefaultSample: SampleFormData = {
  key: 'default-soil-sample',
  sampleType: SampleType.SOIL_SAMPLE,
  ...basicForm,
}

/**
 * Defines a default `SampleFormData` object for a new leaf sample.
 * This structure provides initial values for a form, ensuring consistency.
 */
export const leafDefaultSample: SampleFormData = {
  key: 'default-leaf-sample',
  sampleType: SampleType.LEAF_SAMPLE,
  ...basicForm,
}
